import { motion } from "framer-motion";
import { ArrowRight, LayoutDashboard, Network, BrainCircuit } from "lucide-react";
import { Link } from "react-router-dom";

const cards = [
  {
    id: "core",
    eyebrow: "Core Platform",
    title: "Gradatim MFInsure® Core",
    description:
      "End-to-end core platform for Life and General Insurance — designed to run group, individual and long-term savings products with high-volume scalability and operational control.",
    cta: "Explore MFInsure® Core",
    to: "/products/core",
    Icon: LayoutDashboard,
    palette: {
      from: "#0A192F",
      via: "#1E3A5F",
      to: "#0F2A4A",
      glow: "rgba(56,189,248,0.25)",
      accent: "#7DD3FC",
    },
  },
  {
    id: "grip",
    eyebrow: "Distribution",
    title: "Gradatim MFInsure® GRIP",
    description:
      "Distribution platform enabling partner-led growth across affinity, embedded and bancassurance channels — delivering seamless integration and scalable insurance distribution.",
    cta: "Explore MFInsure® GRIP",
    to: "/products/grip",
    Icon: Network,
    palette: {
      from: "#0F2A4A",
      via: "#0E7490",
      to: "#0A192F",
      glow: "rgba(45,212,191,0.22)",
      accent: "#5EEAD4",
    },
  },
  {
    id: "intelligence",
    eyebrow: "AI Intelligence",
    title: "Gradatim Intelligence®",
    description:
      "AI embedded across underwriting, claims and operations — driving intelligent decision-making, automation and continuous optimization at scale.",
    cta: "Explore Gradatim Intelligence®",
    to: "/products/intelligence",
    Icon: BrainCircuit,
    palette: {
      from: "#1E1B4B",
      via: "#4338CA",
      to: "#0A192F",
      glow: "rgba(167,139,250,0.25)",
      accent: "#C4B5FD",
    },
  },
];

export const Products = () => {
  return (
    <section
      id="products"
      data-testid="platform-section"
      className="border-y border-hairline"
      style={{
        background: "linear-gradient(180deg, #FFFFFF 0%, #F5F7FA 100%)",
      }}
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-24">
        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16 md:mb-20 max-w-3xl mx-auto"
        >
          <div className="mb-6">
            <span className="overline text-copper">The Gradatim Platform</span>
          </div>
          <h2
            data-testid="platform-title"
            className="font-display font-medium text-3xl sm:text-4xl md:text-[44px] tracking-tight leading-[1.1] text-ink mb-5"
          >
            A Unified Platform for{" "}
            <span className="text-copper">Modern Insurance</span>
          </h2>
          <p className="text-base md:text-[18px] text-slate-600 leading-relaxed max-w-[800px] mx-auto">
            Replace fragmented systems with a single, AI-powered, modular
            platform — designed to run, automate and intelligently scale every
            aspect of your insurance business.
          </p>
        </motion.div>

        {/* CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {cards.map((c, i) => (
            <motion.article
              key={c.id}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.1, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="group bg-white border border-hairline rounded-[20px] p-5 flex flex-col transition-all duration-500 hover:-translate-y-1"
              style={{
                boxShadow:
                  "0 8px 24px rgba(2,6,23,0.06), 0 1px 2px rgba(2,6,23,0.04)",
              }}
              data-testid={`platform-card-${c.id}`}
            >
              {/* Visual */}
              <div className="relative h-[180px] rounded-[16px] overflow-hidden mb-5 grain-overlay">
                <div
                  className="absolute inset-0"
                  style={{
                    background: `linear-gradient(135deg, ${c.palette.from} 0%, ${c.palette.via} 55%, ${c.palette.to} 100%)`,
                  }}
                />
                {/* Faint dotted grid */}
                <div
                  aria-hidden
                  className="absolute inset-0 opacity-[0.18]"
                  style={{
                    backgroundImage:
                      "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)",
                    backgroundSize: "16px 16px",
                  }}
                />
                {/* Glow blob */}
                <div
                  aria-hidden
                  className="absolute -bottom-12 -right-12 h-48 w-48 rounded-full blur-3xl opacity-80"
                  style={{ background: c.palette.glow }}
                />
                {/* Decorative SVG per card */}
                <CardArt id={c.id} accent={c.palette.accent} />

                {/* Icon badge */}
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/15 px-2.5 py-1.5 rounded-md">
                  <c.Icon className="h-3.5 w-3.5 text-white" strokeWidth={1.6} />
                  <span className="overline text-white">{c.eyebrow}</span>
                </div>
              </div>

              {/* Title */}
              <h3 className="font-display text-[22px] md:text-[24px] tracking-tight leading-tight text-ink mb-3">
                {c.title}
              </h3>

              {/* Description */}
              <p className="text-[14px] text-slate-600 leading-relaxed flex-1">
                {c.description}
              </p>

              {/* CTA */}
              <div className="mt-6">
                <Link
                  to={c.to}
                  data-testid={`platform-cta-${c.id}`}
                  className="group/btn inline-flex items-center gap-2 bg-ink hover:bg-copper text-white px-4 h-10 rounded-[10px] text-[14px] transition-colors w-full md:w-auto justify-center md:justify-start"
                >
                  {c.cta}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover/btn:translate-x-1" />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * Unique card-art per platform pillar — abstract on-brand SVG visualization
 * inspired by each Canva prompt (Core dashboard, GRIP distribution, Intelligence AI).
 */
const CardArt = ({ id, accent }) => {
  if (id === "core") {
    return (
      <svg viewBox="0 0 360 180" className="absolute inset-0 w-full h-full" aria-hidden>
        {/* Dashboard mock — stacked bars + line */}
        <g opacity="0.9">
          <rect x="30" y="42" width="180" height="86" rx="6" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.15)" />
          <rect x="42" y="54" width="60" height="6" rx="3" fill={accent} />
          <rect x="42" y="68" width="90" height="3" rx="1.5" fill="rgba(255,255,255,0.35)" />
          <rect x="42" y="76" width="70" height="3" rx="1.5" fill="rgba(255,255,255,0.2)" />
          {[0,1,2,3,4,5].map(i => (
            <rect key={i} x={42 + i*22} y={120 - (i%3)*14 - 6} width="12" height={14 + (i%3)*14} rx="2" fill={accent} opacity={0.5 + (i%3)*0.15} />
          ))}
          {/* Side card */}
          <rect x="225" y="42" width="105" height="40" rx="6" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.15)" />
          <circle cx="245" cy="62" r="10" fill={accent} opacity="0.5" />
          <rect x="262" y="56" width="55" height="4" rx="2" fill="rgba(255,255,255,0.5)" />
          <rect x="262" y="64" width="40" height="3" rx="1.5" fill="rgba(255,255,255,0.25)" />
          <rect x="225" y="90" width="105" height="38" rx="6" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.15)" />
          <polyline points="232,118 248,108 264,114 280,98 296,104 312,90 324,98" fill="none" stroke={accent} strokeWidth="2" />
        </g>
      </svg>
    );
  }
  if (id === "grip") {
    // Network distribution graph
    const nodes = [
      { x: 180, y: 90, r: 14 },          // center
      { x: 60, y: 50, r: 8 },
      { x: 70, y: 130, r: 8 },
      { x: 290, y: 55, r: 8 },
      { x: 300, y: 125, r: 8 },
      { x: 180, y: 28, r: 6 },
      { x: 180, y: 152, r: 6 },
    ];
    return (
      <svg viewBox="0 0 360 180" className="absolute inset-0 w-full h-full" aria-hidden>
        {nodes.slice(1).map((n) => (
          <line key={`line-${n.x}-${n.y}`} x1={nodes[0].x} y1={nodes[0].y} x2={n.x} y2={n.y} stroke="rgba(255,255,255,0.25)" strokeWidth="0.8" strokeDasharray="2 3" />
        ))}
        {nodes.map((n, i) => (
          <g key={`node-${n.x}-${n.y}`}>
            <circle cx={n.x} cy={n.y} r={n.r + 6} fill={accent} opacity="0.08" />
            <circle cx={n.x} cy={n.y} r={n.r} fill={i === 0 ? accent : "rgba(255,255,255,0.9)"} opacity={i === 0 ? 0.85 : 0.6} />
          </g>
        ))}
        {/* Pulse dots along an arc */}
        {[40, 90, 140].map((cx, i) => (
          <circle key={`pulse-${cx}`} cx={cx} cy={170 - (i%2)*8} r="2" fill={accent} />
        ))}
      </svg>
    );
  }
  // intelligence — neural lattice
  return (
    <svg viewBox="0 0 360 180" className="absolute inset-0 w-full h-full" aria-hidden>
      {/* Layers */}
      {[60, 130, 200, 270].map((x, li) => (
        [40, 80, 120, 160].map((y, ni) => (
          <circle key={`${li}-${ni}`} cx={x} cy={y} r={li === 0 || li === 3 ? 4 : 3} fill={li === 1 || li === 2 ? accent : "rgba(255,255,255,0.8)"} opacity={0.55 + (ni%2)*0.2} />
        ))
      ))}
      {/* Connections */}
      {[60, 130, 200].map((x, li) => (
        [40, 80, 120, 160].map((y, ai) =>
          [40, 80, 120, 160].map((y2, bi) => (
            <line
              key={`l-${li}-${ai}-${bi}`}
              x1={x}
              y1={y}
              x2={x + 70}
              y2={y2}
              stroke="rgba(255,255,255,0.12)"
              strokeWidth="0.6"
            />
          ))
        )
      ))}
      {/* Brain node */}
      <circle cx="320" cy="90" r="22" fill={accent} opacity="0.35" />
      <circle cx="320" cy="90" r="10" fill={accent} />
    </svg>
  );
};
